import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Alert, Badge } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import userService from '../../services/userService';
import { Formik } from 'formik';
import * as Yup from 'yup';

const UsersManagement = () => {
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  // Define role badge colors
  const roleBadgeColors = {
    admin: 'danger',
    manager: 'info',
    developer: 'primary',
    designer: 'success',
    marketing: 'warning'
  };

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await userService.getAllUsers();
        setUsers(data);
        setFilteredUsers(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Handle navigation state from global search
  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
    }
  }, [location.state]);
  
  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!currentUser) return;

    try {
      await userService.deleteUser(currentUser.id);
      setUsers(users.filter(user => user.id !== currentUser.id));
      setFeedback({
        type: 'success',
        message: `User ${currentUser.name} has been successfully deleted.`
      });
    } catch (err) {
      console.error('Error deleting user:', err);
      setFeedback({
        type: 'danger',
        message: `Failed to delete user. ${err.response?.data?.msg || 'Please try again.'}`
      });
    } finally {
      setShowDeleteModal(false);
      setCurrentUser(null);
    }
  };

  // Validation schemas
  const editUserSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    role: Yup.string().required('Role is required'),
    hourly_rate: Yup.number().min(0, 'Hourly rate must be 0 or more').nullable()
  });

  const passwordSchema = Yup.object({
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  // Modal for editing user
  const renderEditModal = () => {
    if (!currentUser) return null;

    return (
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Formik
          initialValues={{
            name: currentUser.name || '',
            email: currentUser.email || '',
            role: currentUser.role || 'developer',
            hourly_rate: currentUser.hourly_rate || 0
          }}
          validationSchema={editUserSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await userService.updateUser(currentUser.id, values);
              
              // Update the user in the local state
              setUsers(users.map(user => 
                user.id === currentUser.id 
                  ? { ...user, ...values } 
                  : user
              ));
              
              setFeedback({
                type: 'success',
                message: `User ${values.name} has been successfully updated.`
              });
              
              setShowEditModal(false);
            } catch (err) {
              console.error('Error updating user:', err);
              setFeedback({
                type: 'danger',
                message: `Failed to update user. ${err.response?.data?.msg || 'Please try again.'}`
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Edit User</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.name && errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="developer">Developer</option>
                    <option value="designer">Designer</option>
                    <option value="marketing">Marketing</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Hourly Rate (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    name="hourly_rate"
                    value={values.hourly_rate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.hourly_rate && errors.hourly_rate}
                    placeholder="Enter hourly rate"
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.hourly_rate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    );
  };

  // Modal for password reset
  const renderPasswordModal = () => {
    if (!currentUser) return null;

    return (
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Formik
          initialValues={{
            password: '',
            confirmPassword: ''
          }}
          validationSchema={passwordSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              await userService.updatePassword(currentUser.id, values.password);
              
              setFeedback({
                type: 'success',
                message: `Password for ${currentUser.name} has been successfully updated.`
              });
              
              resetForm();
              setShowPasswordModal(false);
            } catch (err) {
              console.error('Error updating password:', err);
              setFeedback({
                type: 'danger',
                message: `Failed to update password. ${err.response?.data?.msg || 'Please try again.'}`
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Reset Password for {currentUser.name}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.password && errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.confirmPassword && errors.confirmPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    );
  };

  // Modal for delete confirmation
  const renderDeleteModal = () => {
    if (!currentUser) return null;

    return (
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete user <strong>{currentUser.name}</strong>?</p>
          <p>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="page-fade-in">
    <Container>
      {/* Search Bar */}
      <Card className="mb-3 shadow-sm" style={{ border: 'none', borderRadius: 12, position: 'sticky', top: '75px', zIndex: 900 }}>
        <Card.Body style={{ padding: '1rem' }}>
          <div className="input-group">
            <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '10px 0 0 10px', border: '2px solid #e9ecef', borderRight: 'none' }}>
              <i className="bi bi-search" style={{ color: '#7C6FA0' }}></i>
            </span>
            <Form.Control
              type="text"
              placeholder="Search users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-start-0 ps-0"
              style={{ 
                borderRadius: '0 10px 10px 0',
                border: '2px solid #e9ecef',
                borderLeft: 'none',
                boxShadow: 'none',
                fontSize: '0.95rem'
              }}
            />
            {searchTerm && (
              <Button 
                variant="link" 
                onClick={() => setSearchTerm('')}
                style={{ position: 'absolute', right: '10px', zIndex: 10, color: '#6c757d' }}
              >
                <i className="bi bi-x-circle"></i>
              </Button>
            )}
          </div>
          {searchTerm && (
            <small className="text-muted mt-2 d-block">
              Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </small>
          )}
        </Card.Body>
      </Card>
      
      {feedback.message && (
        <Alert 
          variant={feedback.type} 
          onClose={() => setFeedback({ type: '', message: '' })} 
          dismissible
          style={{ borderRadius: 10 }}
        >
          {feedback.message}
        </Alert>
      )}
      
      <Row>
        <Col>
          <Card className="mb-4" style={{ border: '1px solid #EEEEEE', borderRadius: 14 }}>
            <Card.Header className="d-flex justify-content-between align-items-center" style={{
              background: 'linear-gradient(135deg, #E8E0F0, #D6E6F5)',
              borderBottom: 'none', borderRadius: '14px 14px 0 0',
              padding: '1rem 1.25rem'
            }}>
              <span style={{ fontWeight: 700, color: '#7C6FA0', fontSize: '0.95rem' }}>All Users</span>
              <Button variant="primary" size="sm" as="a" href="/admin/register" style={{ borderRadius: 50, padding: '0.3rem 1rem' }}>
                Add New User
              </Button>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <p className="text-center" style={{ color: '#9E9E9E', padding: '2rem 0' }}>Loading users...</p>
              ) : error ? (
                <Alert variant="danger">{error}</Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Hourly Rate</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          {searchTerm ? (
                            <div className="py-3">
                              <i className="bi bi-search" style={{ fontSize: '2rem', color: '#dee2e6' }}></i>
                              <p className="mt-2 mb-2">No users match your search criteria.</p>
                              <Button variant="outline-secondary" size="sm" onClick={() => setSearchTerm('')}>
                                Clear Search
                              </Button>
                            </div>
                          ) : (
                            'No users found'
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user, index) => (
                        <tr key={user.id}>
                          <td>{index + 1}</td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            <Badge 
                              bg={roleBadgeColors[user.role] || 'secondary'}
                              text={user.role === 'warning' ? 'dark' : 'white'}
                            >
                              {user.role}
                            </Badge>
                          </td>
                          <td>
                            ₹{parseFloat(user.hourly_rate || 0).toFixed(2)}
                          </td>
                          <td>
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => {
                                setCurrentUser(user);
                                setShowEditModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="me-1"
                              onClick={() => {
                                setCurrentUser(user);
                                setShowPasswordModal(true);
                              }}
                            >
                              Reset Password
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setCurrentUser(user);
                                setShowDeleteModal(true);
                              }}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Render modals */}
      {renderEditModal()}
      {renderPasswordModal()}
      {renderDeleteModal()}
    </Container>
    </div>
  );
};

export default UsersManagement;
